import styled from "@emotion/styled";
import { Flex } from "@radix-ui/themes";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { theme } from "@themes/themes";

import { Accordion, Separator, Text } from "@src/ui-kit";

import { MIN_GTD_LEAD_MS, type TradeboxTimeInForce } from "./hooks/useTradeboxInputs";

import { Checkbox } from "./Checkbox";
import { Icon } from "./Icon";

interface AdvancedAccordionProps {
  timeInForce?: TradeboxTimeInForce;
  onTimeInForceChange?: (value: TradeboxTimeInForce) => void;
  orderType?: "market" | "limit";
  reduceOnly?: boolean;
  onReduceOnlyChange?: (value: boolean) => void;
  gtdExpireAtMs?: number;
  onGtdExpireAtMsChange?: (ms: number) => void;
  gtdExpireInvalid?: boolean;
}

const toDatetimeLocalValue = (ms: number): string => {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromDatetimeLocalValue = (value: string): number => {
  return new Date(value).getTime();
};

export const AdvancedAccordion: React.FC<AdvancedAccordionProps> = observer(
  ({
    timeInForce = "GTC",
    onTimeInForceChange,
    orderType = "limit",
    reduceOnly = false,
    onReduceOnlyChange,
    gtdExpireAtMs,
    onGtdExpireAtMsChange,
    gtdExpireInvalid = false,
  }) => {
    const { t } = useTranslation();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(true);
    const isMarketOrder = orderType === "market";

    useEffect(() => {
      if (isMarketOrder) {
        setIsOpen(false);
      }
    }, [isMarketOrder]);

    const timeInForceOptions: { value: TradeboxTimeInForce; label: string }[] = [
      { value: "GTC", label: t("advanced.gtc") },
      { value: "IOC", label: t("advanced.ioc") },
      { value: "FOK", label: t("advanced.fok") },
      { value: "PostOnly", label: t("advanced.postOnly") },
      { value: "GTD", label: t("advanced.gtd") },
    ];

    const selectedOption = timeInForceOptions.find((opt) => opt.value === timeInForce);

    return (
      <Accordion
        triggerTestId="tradebox:advanced:trigger"
        contentTestId="tradebox:advanced:content"
        disabled={isMarketOrder}
        label={t("advanced.title")}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <Flex direction="column" gap="8px">
          <Separator color="accent-7" size="4" />
          <Flex align="center" gap="16px" justify="between">
            <Checkbox
              data-testid="tradebox:advanced:reduce-only"
              checked={reduceOnly}
              onClick={(value) => {
                onReduceOnlyChange?.(value);
              }}
            >
              <LabelText>{t("advanced.reduceOnly")}</LabelText>
            </Checkbox>
            <SelectWrapper>
              <LabelText>{t("advanced.tif")}</LabelText>
              <SelectContainer>
                <SelectButton
                  data-testid="tradebox:advanced:tif:trigger"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <Text font="body12">{selectedOption?.label}</Text>
                  <Icon
                    color="gray-9"
                    icon={isDropdownOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}
                    size={16}
                  />
                </SelectButton>
                {isDropdownOpen && (
                  <SelectDropdown>
                    {timeInForceOptions.map((option) => (
                      <SelectOption
                        key={option.value}
                        data-testid={`tradebox:advanced:tif:item-${option.value.toLowerCase()}`}
                        active={option.value === timeInForce}
                        onClick={() => {
                          onTimeInForceChange?.(option.value);
                          setIsDropdownOpen(false);
                        }}
                      >
                        <Text font="body12">{option.label}</Text>
                      </SelectOption>
                    ))}
                  </SelectDropdown>
                )}
              </SelectContainer>
            </SelectWrapper>
          </Flex>
          {timeInForce === "GTD" && (
            <GtdRow direction="column" gap="4px">
              <LabelText>{t("advanced.gtdExpire")}</LabelText>
              <GtdDatetimeInput
                data-testid="tradebox:advanced:gtd:expire"
                aria-invalid={gtdExpireInvalid}
                type="datetime-local"
                min={toDatetimeLocalValue(Date.now() + MIN_GTD_LEAD_MS)}
                value={gtdExpireAtMs != null ? toDatetimeLocalValue(gtdExpireAtMs) : ""}
                onChange={(e) => {
                  const next = fromDatetimeLocalValue(e.target.value);
                  if (!Number.isNaN(next)) onGtdExpireAtMsChange?.(next);
                }}
              />
            </GtdRow>
          )}
        </Flex>
      </Accordion>
    );
  },
);

const LabelText = styled.span`
  ${theme.font.body12};
  color: ${theme.color("gray-11")};
`;

const SelectWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  position: relative;
  gap: 3px;
  z-index: 10;
`;

const SelectContainer = styled.div`
  position: relative;
`;

const SelectButton = styled.button`
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 4px 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SelectDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  background: ${theme.color("gray-2")};
  border: 1px solid ${theme.color("gray-7")};
  border-radius: 8px;
  margin-top: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
`;

const GtdRow = styled(Flex)`
  margin-top: 4px;
`;

const GtdDatetimeInput = styled.input`
  background: ${theme.color("gray-3")};
  border: 1px solid ${theme.color("gray-7")};
  border-radius: 8px;
  padding: 6px 10px;
  color: ${theme.color("gray-12")};
  ${theme.font.body12};
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.color("accent-9")};
  }

  &[aria-invalid="true"] {
    border-color: ${theme.color("red-strong")};
  }
`;

const SelectOption = styled.div<{ active?: boolean }>`
  padding: 6px 12px;
  cursor: pointer;
  background: ${({ active }) => (active ? theme.color("gray-4") : "transparent")};
  transition: background 0.2s ease;

  &:hover {
    background: ${theme.color("gray-4")};
  }

  &:first-of-type {
    border-radius: 8px 8px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 8px 8px;
  }
`;
